import { css } from '@emotion/core'
import { graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import React from 'react'

import { AuthorAvatar } from '../../components/AuthorAvatar'
import Block from '../../components/Block'
import Body from '../../components/Body'
import { Button, DemoButton } from '../../components/Button'
import { CardsNavigation } from '../../components/CardsNavigation'
import CTABlock from '../../components/ctas/CTABlock'
import CTAMain from '../../components/ctas/CTAMain'
import Divider from '../../components/Divider'
import { FAQ, FAQContainer } from '../../components/FAQ'
import Footer from '../../components/Footer'
import { HubspotForm } from '../../components/HubspotForm'
import Image from '../../components/Image'
import Layout, { baseFontSize } from '../../components/Layout'
import { Link } from '../../components/Link'
import { LoadingSpinner } from '../../components/LoadingComponent'
import RichText from '../../components/RichText'
import SearchBar, { SearchResultsBlock } from '../../components/Search'
import SEO from '../../components/SEO'
import SignIcon from '../../components/SignIcon'
import { TemplateLinksNavigation } from '../../components/TemplateLinksNavigation'
import TemplateViewsBlock from '../../components/TemplateViewsBlock'
import TextCard from '../../components/TextCard'
import YoutubeVideo from '../../components/YoutubeVideo'
import { breakpoints } from '../../constants/breakPoints'
import { templatesBlockBreakPoint, templatesLpBreakPoint } from '../../constants/breakPoints'
import { isDesktop, isMobile } from '../../helpers/common'
import { richTextRenderingOptions } from '../../helpers/contentful'
import {
  addTemplateToTemplateName,
  getDownloadableTemplateData,
  getLowerCaseTemplateName,
  templatesTextCardFootnote,
} from '../../helpers/templates'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { TemplateProps } from '../../interfaces/templates'
import { searchResultsBlockStyle, TemplatesHeader } from '../../pages/templates'
import { FAQStyle } from '../productHero'
import { templateTextCardStyle } from './TemplateCategory'

export const Template: React.FunctionComponent<TemplateProps> = ({
  data: {
    templatesCategoriesData: { edges: templatesCategories },
    templatesData: { edges: templates },
    template: {
      slug,
      metaTitle,
      metaDescription: { metaDescription },
      abstract: { abstract },
      visual,
      hubspotFormId,
      name,
      creator,
      viewsSectionTitle,
      views,
      downloadableTemplateType,
      youtubeId,
      infoDescription,
      apps,
      benefits,
      infoFaq,
      moreResources,
    },
  },
}) => {
  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebouncedValue(search)

  const { templateType, templateTypeIconSrc } = getDownloadableTemplateData(downloadableTemplateType)

  const focusHubspotForm = () => {
    document.getElementById(`email-${hubspotFormId}`)?.focus()
  }

  // if the template is a product school collaboration, show the product school certificate, ADD to product-school collaboration LP!!!
  const isProductSchoolCollaboration =
    downloadableTemplateType &&
    (slug === 'product-strategy' ||
      slug === 'product-launch' ||
      slug === 'objectives-and-key-results' ||
      slug === 'competitor-analysis')

  // filter searchTerms by (debounced) search term
  const filteredSearchTerms = React.useMemo(
    () =>
      templates
        .filter(({ node: { name } }) => name.toLowerCase().includes(debouncedSearch.toLowerCase()))
        .sort((a, b) => a.node.name.localeCompare(b.node.name)),
    [debouncedSearch]
  )

  return (
    <Layout whiteBG>
      <SEO title={metaTitle} description={metaDescription} imageSrc="templates.png" lang="en" />
      <TemplatesHeader search={search} setSearch={setSearch} />
      <Body>
        {isMobile(templatesLpBreakPoint) && (
          <>
            {!isMobile(breakpoints.small) && (
              <SearchBar isSlidingComponent search={search} setSearch={setSearch} placeholder="Search templates..." />
            )}
            <CardsNavigation
              withSearch
              search={search}
              setSearch={setSearch}
              withBorderBottom
              stickyLayoutOnBreakPoint
              breakPoint={templatesLpBreakPoint}
              hrefPrefix="templates/category"
              categories={[
                { node: { name: 'Featured', slug: 'featured', navigationIcon: {} } },
                ...templatesCategories,
                { node: { name: 'Submit your own', slug: 'submit-own-template', navigationIcon: {} } },
              ]}
            />
          </>
        )}

        <Block
          columnGap={3}
          columnsTemplate="2fr 6fr"
          alignContent="flex-start"
          breakPoint={templatesLpBreakPoint}
          css={containerStyle}
        >
          {!isMobile(templatesLpBreakPoint) && (
            <TemplateLinksNavigation search={search} setSearch={setSearch} categories={templatesCategories} />
          )}
          <div>
            <Block
              columns={2}
              columnsTemplate="2fr 3fr"
              alignContent="stretch"
              columnGap={2}
              css={introStyle(downloadableTemplateType)}
            >
              <div css={introImageContainerStyle}>
                <GatsbyImage image={visual.gatsbyImageData} alt={name} loading="eager" />
                {isProductSchoolCollaboration && isDesktop(templatesBlockBreakPoint + 1) && (
                  <Link
                    className="black-text opacity-on-hover"
                    href="/collaborations/product-school/"
                    css={productSchoolCertificateStyle}
                  >
                    <Image src="templates/product-school-logo.webp" alt="certified by product school" />
                    <div className={isDesktop(breakpoints.xlarge) ? 'heading4' : 'strong-body-1'}>
                      Certified by Product School
                    </div>
                  </Link>
                )}
              </div>
              <div css={introContentStyle}>
                <div>
                  <h1 className="heading2">{addTemplateToTemplateName(name)}</h1>
                  <div className="subtitle">{abstract}</div>
                  {!!downloadableTemplateType && hubspotFormId ? (
                    isDesktop(templatesBlockBreakPoint + 1) && (
                      <div css={hubspotFormStyle}>
                        <HubspotForm hubspotFormId={hubspotFormId} redirect={`/templates/${slug}/thank-you/`} />
                      </div>
                    )
                  ) : (
                    <DemoButton buttonText="Discover it live" />
                  )}
                </div>
                {isProductSchoolCollaboration && isMobile(templatesBlockBreakPoint) && (
                  <Link
                    className="black-text opacity-on-hover"
                    href="/collaborations/product-school/"
                    css={productSchoolCertificateStyle}
                  >
                    <Image src="templates/product-school-logo.webp" alt="certified by product school" />
                    <div className={isDesktop(breakpoints.xlarge) ? 'heading4' : 'strong-body-1'}>
                      Certified by Product School
                    </div>
                  </Link>
                )}
              </div>
            </Block>

            {hubspotFormId && isMobile(templatesBlockBreakPoint) && (
              <>
                <div css={hubspotFormStyle}>
                  <HubspotForm hubspotFormId={hubspotFormId} redirect={`/templates/${slug}/thank-you/`} />
                </div>
                <Divider size={4} notDecreased />
              </>
            )}

            {/* AuthorAvatar is shown above article for tablet & mobile */}
            {creator && isMobile(breakpoints.large) && (
              <>
                <Divider size={4} isTop={false} notDecreased />
                <AuthorAvatar blackBG={false} leftOnSmall withDescription author={creator} />
                <Divider size={4} notDecreased />
              </>
            )}

            <h2 className="heading2 no-margin" css={templatesHeading2Style}>
              {viewsSectionTitle}
            </h2>
            <TemplateViewsBlock
              views={views}
              // downloadable docs templates: preview of the pdf (need margin etc.)
              cssStyles={viewsBlockStyle(downloadableTemplateType)}
            />
            {downloadableTemplateType && (
              <div css={downloadIconsBlockStyle}>
                <div className="strong-body-1">Available as</div>
                <div onClick={focusHubspotForm} css={downloadIconsStyle}>
                  <Image
                    src={`templates/${templateTypeIconSrc}.webp`}
                    alt={`airfocus template as ${templateType} file`}
                  />
                  <div className="body-1 no-margin">{templateType}</div>
                </div>
                <div onClick={focusHubspotForm} css={downloadIconsStyle}>
                  <Image src="templates/pdf-logo.webp" alt="airfocus template as pdf file" />
                  <div className="body-1 no-margin">PDF</div>
                </div>
              </div>
            )}

            {infoDescription && (
              <>
                <Divider size={4} notDecreased />
                {creator && (
                  <h2 className="heading2" css={infoDescriptionHeadingStyle(youtubeId)}>
                    About the {getLowerCaseTemplateName(name.split(' by ')[0])} template
                  </h2>
                )}
                <Block
                  columnsTemplate={creator ? '3fr 1fr' : '1fr'}
                  alignContent="flex-start"
                  columnGap={2}
                  breakPoint={breakpoints.large}
                >
                  <div>
                    {youtubeId && (
                      <div css={expertYoutubeVideoStyle}>
                        <YoutubeVideo id={youtubeId} className="gray-bordered-block" ratio={16 / 9} />
                      </div>
                    )}
                    <RichText css={richTextStyle}>{renderRichText(infoDescription, richTextRenderingOptions)}</RichText>
                  </div>
                  {creator && !isMobile(breakpoints.large) && (
                    <AuthorAvatar
                      blackBG={false}
                      author={creator}
                      withDescription
                      isInSidebar
                      cssStyles={avatarStyle}
                    />
                  )}
                </Block>
              </>
            )}

            <CTABlock
              heading={`Get started with the ${getLowerCaseTemplateName(name.split(' by ')[0])} template now`}
              withoutSubtitle
              cssStyles={ctaBlockStyle}
              primaryButton={
                downloadableTemplateType && hubspotFormId ? (
                  <Button type="primary" onClick={focusHubspotForm}>
                    <p className="strong-body-2">Get this template</p>
                  </Button>
                ) : (
                  <DemoButton buttonText="Discover it live" />
                )
              }
            />

            {apps && (
              <React.Fragment>
                <h2 className="heading2 no-margin" css={templatesHeading2Style}>
                  Pre-installed apps
                </h2>
                <Block
                  columns={2}
                  rowGap={2}
                  columnsTemplate={`repeat(${apps.length > 1 ? '2' : '1'}, 1fr)`}
                  css={appBlockStyle}
                >
                  {apps.map(({ name, description: { description }, appImage }) => (
                    <Block key={name} columns={2} columnsTemplate="48px auto" css={appStyle(apps.length)}>
                      {appImage && <GatsbyImage image={appImage.gatsbyImageData} alt={appImage.title} />}
                      <div>
                        <h4 className="heading4">{name}</h4>
                        <p className="subtitle">{description}</p>
                      </div>
                    </Block>
                  ))}
                </Block>
                <Divider size={4} color="transparent" />
              </React.Fragment>
            )}

            <h2 className="heading2 no-margin" css={templatesHeading2Style}>
              Benefits that come from using this template
            </h2>
            <Block className="subtitle" columns={2} alignContent="flex-start" rowGap={1} noBreakForTablet>
              {benefits.map(({ benefitText }) => (
                <Block
                  key={benefitText}
                  columns={2}
                  columnsTemplate="auto 1fr"
                  alignContent="flex-start"
                  columnGap={0.5}
                  noBreak
                >
                  <SignIcon type="checkmark" />
                  <p>{benefitText}</p>
                </Block>
              ))}
            </Block>

            {infoFaq && infoFaq.length > 0 && (
              <>
                <Divider size={6} />
                <h2 className="heading2" css={templatesHeading2Style}>
                  FAQ about the {getLowerCaseTemplateName(name)} template
                </h2>
                <div className="rich-text" css={[FAQContainer, FAQStyle]}>
                  {infoFaq.map(({ question: { question }, answer: { answer } }, index) => (
                    <FAQ withBlueChevron key={index} question={question} answer={answer} />
                  ))}
                </div>
              </>
            )}

            {!(creator || downloadableTemplateType) && (
              <>
                <Divider size={6} />
                <h2 className="heading2 no-margin" css={templatesHeading2Style}>
                  Learn how to use airfocus templates effectively
                </h2>
                <div css={youtubeVideoStyle}>
                  <YoutubeVideo id="5ghpOynGuLo" className="gray-bordered-block" ratio={16 / 9} />
                </div>
              </>
            )}
          </div>
        </Block>

        <Divider size={8} />

        {moreResources && (
          <React.Fragment>
            <h2 className="heading2">More templates</h2>
            <Block columns={4} alignContent="stretch" twoColumnsForTablet css={moreTemplatesBlockStyle}>
              {moreResources.map(
                ({ name, slug, downloadableTemplateType, workspacesCount, abstract, visual }, index) => (
                  <TextCard
                    className={index === 3 ? 'hide-on-small hide-on-large' : ''}
                    gatsbyImageSrc={visual}
                    title={name}
                    footnote={templatesTextCardFootnote(workspacesCount, downloadableTemplateType)}
                    description={abstract.abstract}
                    linkUrl={`/templates/${slug}/`}
                    key={`template-card-${slug}`}
                    cssStyles={templateTextCardStyle}
                  />
                )
              )}
            </Block>
          </React.Fragment>
        )}

        <CTAMain instantTourVersion />

        {/* for mobile: Spinner is loaded from <Header.tsx /> */}
        {isMobile(templatesLpBreakPoint) && !isMobile(breakpoints.small) && search && <LoadingSpinner />}

        {/* slide-up for search results (overlay) */}
        <SearchResultsBlock
          useContainerOnly
          withSearchBar={!isMobile(templatesLpBreakPoint)}
          search={search}
          setSearch={setSearch}
          debouncedSearch={debouncedSearch}
          resultsArray={filteredSearchTerms}
          resultsBlockContent={
            <Block
              columns={4}
              alignContent="flex-start"
              breakPoint={templatesBlockBreakPoint}
              twoColumnsForTablet
              rowGapMobile={2}
              css={searchResultsBlockStyle}
            >
              {filteredSearchTerms.map(({ node: filteredTemplate }, index) => (
                <TextCard
                  key={`${debouncedSearch}-${filteredTemplate.slug}-${index}`}
                  gatsbyImageSrc={filteredTemplate.visual}
                  title={filteredTemplate.name}
                  footnote={templatesTextCardFootnote(
                    filteredTemplate.workspacesCount,
                    filteredTemplate.downloadableTemplateType
                  )}
                  description={filteredTemplate.abstract.abstract}
                  linkUrl={`/templates/${filteredTemplate.slug}/`}
                  cssStyles={templateTextCardStyle}
                />
              ))}
            </Block>
          }
        />
      </Body>
      <Footer />
    </Layout>
  )
}

export default Template

export const query = graphql`
  query ($id: String!) {
    templatesCategoriesData: allContentfulProductTemplateCategory(
      filter: { slug: { ne: "others" } }
      sort: { fields: order, order: ASC }
    ) {
      edges {
        node {
          navigationIcon {
            title
            gatsbyImageData(width: 60, quality: 90)
          }
          order
          id
          slug
          name
        }
      }
    }

    templatesData: allContentfulProductTemplate(
      filter: { id: { ne: "15363eff-a7cb-5b42-b83d-8898b1bb543d" } }
      sort: { fields: order, order: ASC }
    ) {
      edges {
        node {
          abstract {
            abstract
          }
          name
          workspacesCount
          downloadableTemplateType
          featured
          slug
          visual {
            gatsbyImageData(width: 600, quality: 90)
            title
          }
        }
      }
    }

    template: contentfulProductTemplate(id: { eq: $id }) {
      slug
      metaTitle
      metaDescription {
        metaDescription
      }
      viewsSectionTitle
      abstract {
        abstract
      }
      visual {
        gatsbyImageData(width: 500, quality: 90)
      }
      name
      creator {
        name
        jobTitle
        companyName
        description {
          description
        }
        slug
        authorPhoto {
          gatsbyImageData(width: 80, quality: 90)
        }
        authorPublications: product_template {
          id
        }
      }
      downloadableTemplateType
      hubspotFormId
      apps {
        typeId
        name
        description {
          description
        }
        appImage {
          title
          gatsbyImageData(width: 48, height: 48, quality: 90)
        }
      }
      viewsSectionTitle
      views {
        name
        iconName
        preview {
          gatsbyImageData(layout: FULL_WIDTH, quality: 90)
        }
      }
      benefits {
        benefitText
      }
      youtubeId
      infoDescription {
        raw
      }
      infoFaq {
        question {
          question
        }
        answer {
          answer
        }
      }
      moreResources {
        slug
        visual {
          gatsbyImageData(width: 400, quality: 90)
        }
        name
        creator {
          name
          authorPhoto {
            gatsbyImageData(width: 60, quality: 90)
          }
        }
        abstract {
          abstract
        }
        downloadableTemplateType
        workspacesCount
      }
    }
  }
`

const containerStyle = css`
  margin-top: 4rem;

  @media screen and (max-width: ${breakpoints.xlarge}px) {
    grid-template-columns: 249px auto;
  }

  @media screen and (max-width: ${templatesLpBreakPoint}px) {
    grid-template-columns: 1fr;
    row-gap: 0;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    margin-top: 3rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    margin-top: 0;
  }
`

const introStyle = (downloadableTemplateType?: string) => css`
  margin-bottom: 6rem;

  .gatsby-image-wrapper {
    border-radius: 0.5rem;
  }

  .heading2 {
    margin: -0.5rem 0 0;
  }

  .subtitle {
    margin: 0.5rem 0 0.75rem;
  }

  ${downloadableTemplateType &&
  `
    @media screen and (max-width: ${templatesBlockBreakPoint}px) {
      margin-bottom: 0;
    }
  `}

  @media screen and (max-width: ${breakpoints.medium}px) {
    grid-template-columns: 1fr 1.2fr;
    margin-bottom: 3rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    grid-template-columns: 1fr;

    .gatsby-image-wrapper {
      margin: 0 -1.5rem;
      border-radius: 0;
      aspect-ratio: 1.6;
    }

    .heading2 {
      margin: 0;
    }
  }
`

const introImageContainerStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media screen and (min-width: ${templatesLpBreakPoint + 1}px) {
    // to keep "Certified by Product School a 1-liner"
    min-width: 253px;
  }
`

const productSchoolCertificateStyle = css`
  margin: 1rem 0 0.25rem;
  display: flex;
  gap: 0.75rem;
  align-items: center;

  .gatsby-image-wrapper {
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    // to overrule the margin: 0 -1.5rem from the intro style above
    margin: initial !important;
  }
`

const introContentStyle = (downloadableTemplateType?: string) => css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;

  ${downloadableTemplateType &&
  `
    @media screen and (min-width: ${breakpoints.medium + 1}px) and (max-width: ${templatesBlockBreakPoint}px) {
      min-width: 462px;
    }
  `}
`

const hubspotFormStyle = css`
  form {
    margin: 0;

    .actions > input {
      width: 100%;
      height: 2.75rem;
      font-size: 0.875rem !important;
    }

    @media screen and (max-width: ${templatesBlockBreakPoint}px) {
      margin: 4rem auto 0;
    }

    @media screen and (max-width: ${breakpoints.small}px) {
      margin-top: 2rem;
    }
  }
`

// export for templates.tsx & TemplateCategory.tsx
export const templatesHeading2Style = css`
  &.heading2 {
    font-size: ${(30 / baseFontSize).toFixed(3)}rem !important;
    line-height: ${(40 / baseFontSize).toFixed(3)}rem !important;
    margin: ${(32 / baseFontSize).toFixed(3)}rem 0 ${(16 / baseFontSize).toFixed(3)}rem;
  }
`

const appBlockStyle = css`
  margin-top: 2rem;

  @media screen and (max-width: ${breakpoints.large}px) {
    grid-template-columns: 1fr;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    grid-template-columns: 1fr 1fr;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    grid-template-columns: 1fr;
    row-gap: 1.5rem;
  }
`

const viewsBlockStyle = (downloadableTemplateType?: string) => css`
  ${downloadableTemplateType === 'Docs Template' &&
  `
    .gray-bordered-block {
      max-height: 20rem;
    }
  `}

  ${downloadableTemplateType &&
  `
    margin-bottom: 2rem;

    .gray-bordered-block {
      background: #fafbfc;
      overflow: scroll;

      .gatsby-image-wrapper {
        border-radius: 0.25rem;
        box-shadow: rgb(0 0 0 / 8%) 0 0.5rem 2rem;
        margin: 2rem auto;
        max-width: 70%;

        @media screen and (max-width: ${templatesLpBreakPoint}px) {
          max-width: 80%;
        }

        @media screen and (max-width: ${breakpoints.small}px) {
          margin: 0;
          max-width: 100%;
        }
      }
    }
  `}
`

const downloadIconsBlockStyle = css`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const downloadIconsStyle = css`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .gatsby-image-wrapper {
    transition: all 0.2s ease;
    width: 2rem;
    height: 2rem;
  }

  &:hover {
    .gatsby-image-wrapper {
      transform: scale(1.03);
    }

    .body-1 {
      opacity: 0.6;
    }
  }
`

const richTextStyle = css`
  margin: 0;

  @media screen and (max-width: ${templatesLpBreakPoint}px) {
    margin: auto;
  }

  & > :last-child {
    margin-bottom: 0;
  }
`

const infoDescriptionHeadingStyle = (youtubeId?: string) => css`
  margin-top: 0 !important;

  ${!youtubeId &&
  `
    max-width: 800px;
    margin-left: auto !important;
    margin-bottom: 2rem !important;
    margin-right: auto !important;
  `}
`

const expertYoutubeVideoStyle = css`
  margin-bottom: 2rem;
`

const youtubeVideoStyle = css`
  margin: 2rem 0;
`

const avatarStyle = css`
  position: sticky;
  top: 6.5rem;
`

const appStyle = (appsLength: number) => css`
  .gatsby-image-wrapper {
    height: 3rem;
    width: 3rem;
  }
  .subtitle {
    margin: 0;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    ${appsLength === 1 && 'grid-template-columns: min-content max-content'};
    row-gap: 0.5rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    grid-template-columns: min-content auto;
  }
`

const ctaBlockStyle = css`
  min-height: initial;
  margin: 8rem 0;

  @media screen and (min-width: ${breakpoints.medium + 1}px) {
    & > div:first-of-type {
      padding-left: 3.5rem;
    }
  }

  .heading2 {
    margin-bottom: 1.5rem;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    margin: 6rem 0;
  }
`

const moreTemplatesBlockStyle = css`
  @media screen and (min-width: ${breakpoints.medium + 1}px) and (max-width: ${breakpoints.large}px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    row-gap: 2rem;
  }
`
